import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_VIEW,
  SIGNAL_BANDS,
  formatAge,
  hexCellKm,
  hexResolutionForZoom,
  type HexCell,
  type LiveEvent,
  type NodeRecord,
  type PositionPoint,
  type Stats,
  type Summary,
} from '@mcoverage/shared';
import { MapView, type LayerMode } from './components/MapView';
import { Sidebar } from './components/Sidebar';
import { NodePanel } from './components/NodePanel';
import {
  ChevronIcon,
  CrosshairIcon,
  FilterIcon,
  LogoIcon,
  SearchIcon,
} from './components/icons';
import { DEFAULT_FILTERS, api, type FiltersState } from './lib/api';
import { useLiveEvents } from './lib/live';
import './styles.css';

const REFRESH_DEBOUNCE_MS = 600;

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function App() {
  const [filters, setFilters] = useState<FiltersState>(DEFAULT_FILTERS);
  const [bounds, setBounds] = useState<string>('');
  const [zoom, setZoom] = useState(DEFAULT_VIEW.zoom);
  const [nodes, setNodes] = useState<NodeRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [hexes, setHexes] = useState<HexCell[]>([]);
  const [coverage, setCoverage] = useState<HexCell[]>([]);
  const [coverageSamples, setCoverageSamples] = useState(0);
  const [stats, setStats] = useState<Stats | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  // Alles, was es zu zeigen gibt: Nodes, Dichte und gemessene Coverage. Wer
// eine einzelne Ebene braucht, blendet ueber den Schalter darueber ab.
const [layerMode, setLayerMode] = useState<LayerMode>('all');
  const [showPrecision, setShowPrecision] = useState(false);
  const [selected, setSelected] = useState<NodeRecord | null>(null);
  const [trackNode, setTrackNode] = useState<number | null>(null);
  // Playback head is owned here because both the slider (NodePanel) and the
  // drawn route (MapView) need it.
  const [playhead, setPlayhead] = useState<number | null>(null);
  const [track, setTrack] = useState<PositionPoint[] | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [liveConnected, setLiveConnected] = useState(false);
  // Auf dem Desktop ist die Legende aufgeklappt, auf schmalen Fenstern zu --
  // dort frisst sie sonst den halben Bildschirm und verdeckt die Ebenenwahl.
  // matchMedia fehlt in jsdom, deshalb der Guard.
  const [legendOpen, setLegendOpen] = useState(
    () =>
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function' ||
      !window.matchMedia('(max-width: 720px)').matches,
  );

  const debouncedFilters = useDebounced(filters, REFRESH_DEBOUNCE_MS);
  const debouncedBounds = useDebounced(bounds, REFRESH_DEBOUNCE_MS);
  const debouncedZoom = useDebounced(zoom, REFRESH_DEBOUNCE_MS);
  // Beide Hex-Ebenen folgen der Zoomstufe: draussen grobe Zellen, beim
  // Reinzoomen feinere. Entprellt wie die Bounds, sonst feuert jeder Frame
  // einer Geste-Zoom-Animation einen eigenen Request.
  const hexResolution = useMemo(() => hexResolutionForZoom(debouncedZoom), [debouncedZoom]);
  const hexCellLabel = useMemo(() => {
    const km = hexCellKm(hexResolution);
    return km >= 1 ? `~${km.toFixed(1).replace('.', ',')} km` : `~${Math.round(km * 1000)} m`;
  }, [hexResolution]);
  const nodesRef = useRef<Map<number, NodeRecord>>(new Map());

  // --- data loading --------------------------------------------------------
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    api
      .nodes(debouncedFilters, debouncedBounds || undefined, controller.signal)
      .then((res) => {
        const list = Array.isArray(res.nodes) ? res.nodes : [];
        setNodes(list);
        setTotal(res.total ?? list.length);
        setTruncated(Boolean(res.truncated));
        nodesRef.current = new Map(list.map((n) => [n.nodeNum, n]));
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [debouncedFilters, debouncedBounds]);

  useEffect(() => {
    const controller = new AbortController();
    api
      .summary(debouncedFilters, debouncedBounds || undefined, controller.signal)
      .then(setSummary)
      .catch(() => {});
    return () => controller.abort();
  }, [debouncedFilters, debouncedBounds]);

  useEffect(() => {
    const controller = new AbortController();
    api
      .hexes(hexResolution, debouncedBounds || undefined, controller.signal)
      .then((res) => setHexes(Array.isArray(res.cells) ? res.cells : []))
      .catch(() => {});
    return () => controller.abort();
  }, [debouncedBounds, hexResolution]);

  // Measured coverage is empty until somebody maps, so failures are expected
  // and must stay silent.
  useEffect(() => {
    const controller = new AbortController();
    api
      .coverage(hexResolution, debouncedBounds || undefined, controller.signal)
      .then((res) => {
        setCoverage(Array.isArray(res.cells) ? res.cells : []);
        setCoverageSamples(res.totalSamples ?? 0);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [debouncedBounds, hexResolution]);

  // Stats arrive over SSE every 10s, so this only seeds the initial value.
  useEffect(() => {
    const controller = new AbortController();
    api.stats(controller.signal).then(setStats).catch(() => {});
    return () => controller.abort();
  }, []);

  // --- live updates --------------------------------------------------------
  const handleEvent = useCallback((event: LiveEvent) => {
    if (event.type === 'node') {
      const map = nodesRef.current;
      const known = map.get(event.node.nodeNum);
      if (known) {
        map.set(event.node.nodeNum, { ...known, ...event.node });
      }
      setNodes([...map.values()]);
    } else if (event.type === 'stats') {
      setStats(event.stats);
    } else {
      setLiveConnected(true);
    }
  }, []);

  useLiveEvents(handleEvent);

  // --- track loading -------------------------------------------------------
  useEffect(() => {
    if (trackNode === null) {
      setTrack(null);
      setPlayhead(null);
      return;
    }
    const controller = new AbortController();
    api
      .node(trackNode, controller.signal)
      .then((d) => setTrack(d.track))
      .catch(() => {});
    return () => controller.abort();
  }, [trackNode]);

  const onSelect = useCallback((node: NodeRecord | null) => {
    setSelected(node);
    setTrackNode(node ? node.nodeNum : null);
  }, []);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.hardware.length) n++;
    if (filters.roles.length) n++;
    if (filters.regions.length) n++;
    if (filters.onlineOnly) n++;
    if (filters.includeHidden) n++;
    if (filters.batteryMin > 0) n++;
    if (filters.search.trim()) n++;
    if (filters.seenWithinHours !== DEFAULT_FILTERS.seenWithinHours) n++;
    return n;
  }, [filters]);

  const num = (n: number | undefined) => (n ?? 0).toLocaleString('de-DE');

  // Node names only arrive with a node's (infrequent) node-info broadcast, so
  // showing the ratio keeps a half-empty map from looking broken.
  const namedCount = useMemo(
    () => nodes.filter((n) => n.longName !== '' || n.shortName !== '').length,
    [nodes],
  );

  return (
    <div className="app">
      <MapView
        nodes={nodes}
        hexes={hexes}
        hexResolution={hexResolution}
        coverage={coverage}
        track={track}
        playhead={playhead}
        layerMode={layerMode}
        showPrecision={showPrecision}
        selected={selected}
        onSelect={onSelect}
        onBoundsChange={setBounds}
        onZoomChange={setZoom}
        loading={loading}
        truncated={truncated}
        total={total}
      />

      <div className="hud">
        <div className="hud-top">
          <div className="hud-tl">
            <div className="card brand-card">
              <div className="brand-row">
                <span className="brand-mark">
                  <LogoIcon />
                </span>
                <h1>Meshtastic Coverage</h1>
                <span className={liveConnected ? 'live on' : 'live off'} title="Live-Datenstrom">
                  <i aria-hidden /> LIVE
                </span>
              </div>

              <div className="search-row">
                <div className="search">
                  <SearchIcon />
                  <input
                    type="search"
                    placeholder="Node suchen…"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    aria-label="Node suchen"
                  />
                </div>
                <button
                  type="button"
                  className="filter-btn"
                  aria-expanded={sheetOpen}
                  aria-controls="filter-sheet"
                  onClick={() => setSheetOpen((v) => !v)}
                >
                  <FilterIcon /> Filter
                  {activeFilterCount > 0 && <em>{activeFilterCount}</em>}
                </button>
              </div>

              <dl className="hud-stats">
                <div>
                  <dt>Nodes</dt>
                  <dd>{num(stats?.nodes)}</dd>
                </div>
                <div>
                  <dt>Online</dt>
                  <dd className="on">{num(stats?.nodesOnline)}</dd>
                </div>
                <div>
                  <dt>Mit Position</dt>
                  <dd>{num(stats?.nodesWithPosition)}</dd>
                </div>
              </dl>

              <div className="hud-meta">
                <span>
                  {num(summary?.total)} Nodes im Ausschnitt
                  {truncated ? ` von ${num(total)}` : ''}
                </span>
                {nodes.length > 0 && (
                  <span title="Namen erscheinen, sobald die Nodes ihre NodeInfo senden (teils erst nach Stunden)">
                    Namen {num(namedCount)}/{num(nodes.length)}
                  </span>
                )}
                <span title="Gemessene Funkabdeckung aus dem Mapper">
                  Coverage: {num(coverageSamples)} Messungen
                </span>
                <span title="Zeitpunkt der letzten über MQTT empfangenen Positionsdaten">
                  {stats?.lastIngestAt
                    ? `Positionsdaten ${formatAge(Date.now() / 1000 - stats.lastIngestAt / 1000)}`
                    : 'noch keine Positionsdaten'}
                </span>
              </div>
            </div>
          </div>

          <div className="hud-tr">
            <div className="card layer-card">
              <div className="layer-switch" role="group" aria-label="Ebene">
                <button
                  type="button"
                  className={layerMode === 'all' ? 'active' : ''}
                  onClick={() => setLayerMode('all')}
                >
                  Alles
                </button>
                <button
                  type="button"
                  className={layerMode === 'nodes' ? 'active' : ''}
                  onClick={() => setLayerMode('nodes')}
                >
                  Nodes
                </button>
                <button
                  type="button"
                  className={layerMode === 'both' ? 'active' : ''}
                  onClick={() => setLayerMode('both')}
                >
                  Beides
                </button>
                <button
                  type="button"
                  className={layerMode === 'hex' ? 'active' : ''}
                  onClick={() => setLayerMode('hex')}
                >
                  Dichte
                </button>
                <button
                  type="button"
                  className={layerMode === 'coverage' ? 'active' : ''}
                  onClick={() => setLayerMode('coverage')}
                  title={coverageSamples === 0 ? 'Noch keine Messungen vorhanden' : undefined}
                >
                  Coverage
                </button>
              </div>

              <label className="toggle">
                <input
                  type="checkbox"
                  checked={showPrecision}
                  onChange={(e) => setShowPrecision(e.target.checked)}
                />
                <CrosshairIcon />
                Genauigkeit
              </label>

              <p
                className="layer-note"
                title="Dichte und Coverage werden mit der Zoomstufe feiner: die Zellen behalten dabei ungefähr ihre Größe auf dem Bildschirm."
              >
                Raster {hexResolution} · {hexCellLabel}
              </p>
            </div>
          </div>
        </div>

        <div className="hud-bl">
          <details
            className="card legend-card"
            open={legendOpen}
            onToggle={(e) => setLegendOpen(e.currentTarget.open)}
          >
            <summary>
              Legende
              <ChevronIcon />
            </summary>
            <div className="legend-body">
              <p className="legend-title">Signalstärke (RSSI)</p>
              <div className="ramp" aria-hidden>
                {SIGNAL_BANDS.map((b) => (
                  <span key={b.band} style={{ background: b.color }} />
                ))}
              </div>
              <div className="ramp-labels">
                <span>gut</span>
                <span>schlecht</span>
              </div>

              <ul className="legend-list">
                <li>
                  <span className="swatch" style={{ background: '#22c55e' }} /> Online
                </li>
                <li>
                  <span className="swatch faded" style={{ background: '#64748b' }} /> Länger nicht
                  gehört
                </li>
                <li>
                  <span className="swatch ring" style={{ borderColor: '#a78bfa' }} /> Tracker
                </li>
                <li>
                  <span className="swatch ring" style={{ borderColor: '#0ea5e9' }} /> Repeater/Router
                </li>
              </ul>

              <p className="legend-note">
                Füllfarbe = RSSI des letzten Pakets. <strong>Genauigkeit</strong> blendet den
                Unsicherheitskreis ein.
              </p>
            </div>
          </details>
        </div>

        <div
          className={sheetOpen ? 'scrim on' : 'scrim'}
          onClick={() => setSheetOpen(false)}
          aria-hidden
        />

        <Sidebar
          id="filter-sheet"
          stats={stats}
          summary={summary}
          filters={filters}
          onChange={setFilters}
          onReset={() => setFilters(DEFAULT_FILTERS)}
          open={sheetOpen}
          onToggle={() => setSheetOpen((v) => !v)}
          activeCount={activeFilterCount}
        />

        {selected && (
          <NodePanel
            nodeNum={selected.nodeNum}
            onClose={() => onSelect(null)}
            playhead={playhead}
            onPlayheadChange={setPlayhead}
          />
        )}
      </div>
    </div>
  );
}
